import { Link } from 'react-router-dom'
import { Calendar, ArrowRight } from 'lucide-react'
import { SEO, BreadcrumbStructuredData } from '@/components/seo/SEO'
import { getAllBlogs } from '@/data/blogs'

export function BlogsPage() {
  const blogs = getAllBlogs()

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blogs', url: '/blogs' },
  ]

  return (
    <>
      <SEO
        title="Blogs"
        description="Read the latest Polytoria news, product updates, and creator highlights."
        url="/blogs"
        type="website"
      />
      <BreadcrumbStructuredData items={breadcrumbs} />

      {/* Hero Banner - Full Width Outside Container */}
      <div className="relative w-full h-48 md:h-[40vh] overflow-hidden">
        <img
          src="/images/32105.webp"
          alt="Polytoria Blog Banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center max-w-[1400px] mx-auto px-6 lg:px-20 pt-28">
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-2">Blogs</h1>
          <p className="text-white/70 text-sm md:text-base max-w-md">
            Explore {blogs.length} posts covering updates, guides, and community highlights
          </p>
        </div>
      </div>

      <div className="min-h-screen bg-background-dark">
        <main className="max-w-[1400px] mx-auto px-6 lg:px-20 py-20">

          {blogs.length === 0 ? (
            <div className="glass-panel rounded-2xl p-8 border border-border-dark">
              <p className="text-slate-300 font-bold">No blog posts yet.</p>
              <p className="text-slate-500 text-sm mt-1">
                Add JSON files in <span className="text-slate-300">src/data/blogs</span> to publish.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {blogs.map((blog) => (
                <Link
                  key={blog.slug}
                  to={`/blogs/${blog.slug}`}
                  className="glass-panel group rounded-2xl overflow-hidden border border-border-dark hover:border-game-primary/40 transition-colors flex flex-col h-full"
                >
                  <div className="aspect-[16/9] bg-card-dark overflow-hidden">
                    {blog.coverImage ? (
                      <img
                        src={blog.coverImage}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-white/5" />
                    )}
                  </div>

                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      {blog.publishedAt && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-game-primary" />
                          {new Date(blog.publishedAt).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      )}
                      {blog.readingTime && <span>• {blog.readingTime}</span>}
                    </div>

                    <h2 className="text-white text-lg font-black mt-3 leading-snug group-hover:text-game-primary transition-colors">
                      {blog.title}
                    </h2>

                    {blog.excerpt && (
                      <p className="text-slate-400 text-sm mt-2 leading-relaxed line-clamp-3">
                        {blog.excerpt}
                      </p>
                    )}

                    <div className="flex items-center justify-start mt-auto pt-5">
                      <span className="inline-flex items-center gap-1 text-game-primary font-bold text-sm">
                        Read <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  )
}
